import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IIntegration, IntegrationType } from '@principle-theorem/integrations';
import {
  isDisabled$,
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { ViaApiIntegrationStorage } from '@principle-theorem/principle-core';
import {
  IViaApiIntegrationData,
  SmsIntegrationOption,
} from '@principle-theorem/principle-core/interfaces';
import { CollectionReference, snapshot } from '@principle-theorem/shared';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { IsManagementDirective } from '@principle-theorem/ng-principle-shared';

type ManagementFormData = Pick<IViaApiIntegrationData, 'enabled'>;

@Component({
  selector: 'pr-manage-via-api-integration',
  templateUrl: './manage-via-api-integration.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatSlideToggle,
    MatButtonModule,
    IsManagementDirective,
  ],
})
export class ManageViaApiIntegrationComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _integrationsCol$ = new ReplaySubject<
    CollectionReference<IIntegration<IViaApiIntegrationData>>
  >(1);
  private _viaApiStorage = new ViaApiIntegrationStorage();
  private _savedProvider$ = new ReplaySubject<SmsIntegrationOption | undefined>(
    1
  );
  enabled$: Observable<boolean>;
  managementForm = new TypedFormGroup<ManagementFormData>({
    enabled: new TypedFormControl<boolean>(false),
  });
  isDisabled$ = isDisabled$(this.managementForm);

  @Output() providerChanged = new EventEmitter<SmsIntegrationOption>();

  @Input()
  set integrationsCol(
    integrationsCol: CollectionReference<IIntegration<IViaApiIntegrationData>>
  ) {
    if (!integrationsCol) {
      return;
    }
    this._integrationsCol$.next(integrationsCol);
  }

  @Input()
  set savedProvider(provider: SmsIntegrationOption | undefined) {
    this._savedProvider$.next(provider);
  }

  constructor(private _snackBar: MatSnackBar) {
    this.enabled$ = this._integrationsCol$.pipe(
      switchMap((integrationsCol) =>
        this._viaApiStorage.get$(integrationsCol)
      ),
      map((integration) => !!integration?.data.enabled)
    );

    const data$ = this._integrationsCol$.pipe(
      switchMap((integrationsCol) =>
        this._viaApiStorage.get$(integrationsCol)
      ),
      map((data) => data?.data ?? ViaApiIntegrationStorage.init({}))
    );

    data$.pipe(take(1), takeUntil(this._onDestroy$)).subscribe((data) => {
      this.managementForm.patchValue(data, { emitEvent: false });
    });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async save(): Promise<void> {
    const savedProvider = await snapshot(this._savedProvider$);
    if (savedProvider !== IntegrationType.ViaApi) {
      this.providerChanged.emit(IntegrationType.ViaApi);
    }
    const col = await snapshot(this._integrationsCol$);
    const managementFormData = this.managementForm.getRawValue();
    await this._viaApiStorage.upsert(col, managementFormData);
    this._snackBar.open('Settings Updated');
  }
}
